import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../BlogPostPage'; // Reusable dark-themed split layout

// --- STATIC METADATA & CONTENT ---
const BLOG_SLUG = "lokale-seo-handwerker-sueddeutschland";
const heroImageUrl = "https://images.pexels.com/photos/5711209/pexels-photo-5711209.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2";
const articleImageUrl = "https://images.pexels.com/photos/220357/pexels-photo-220357.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2";

const tableOfContents = [
  { id: 'problem', title: 'Das Problem: Unsichtbarkeit kostet Kunden' },
  { id: 'google-my-business', title: 'Google My Business: Ihr Tor zur lokalen Sichtbarkeit' },
  { id: 'lokale-citations', title: 'Lokale Citations: Vertrauen und Konsistenz aufbauen' },
  { id: 'content-strategie', title: 'Content-Strategie: Relevante Inhalte für lokale Kunden' },
  { id: 'rezensionen', title: 'Die Macht der Rezensionen' },
  { id: 'enzlo-loesung', title: 'Enzlo: Ihre All-in-One-Lösung' },
  { id: 'fazit', title: 'Fazit: Jetzt ist die Zeit zu handeln' }
];

const BlogPost_HandwerkerSEO = () => {
  const { t } = useTranslation('blogPostHandwerkerSEO');
  const [openFaq, setOpenFaq] = React.useState(null);

  // --- DYNAMIC CONTENT FROM TRANSLATIONS ---
  const title = t('meta.title');
  const description = t('meta.description');
  const category = { name: t('meta.category'), slug: "seo" };
  const date = t('meta.date');
  const author = t('meta.author');
  const readTime = t('meta.readTime');

  const canonicalUrl = `https://enzlo.com/blog/${BLOG_SLUG}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": heroImageUrl,
    "author": {
      "@type": "Organization",
      "name": "Enzlo"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Enzlo",
      "logo": {
        "@type": "ImageObject",
        "url": "https://enzlo.com/logo.png"
      }
    },
    "datePublished": "2025-07-11",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    }
  };

  return (
    <div className="split-container">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={heroImageUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={heroImageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="split-hero">
        <div className="hero-left">
          <div className="split-category">{category.name}</div>
          <h1 className="split-title">{title}</h1>
          <p className="split-excerpt">{description}</p>
          <div className="split-meta">{date} • {author} • {readTime} {t('meta.readTimeSuffix', 'Lesezeit')}</div>
        </div>
        <img src={heroImageUrl} alt={t('hero.alt', 'Professioneller Handwerker bei der Arbeit')} className="hero-image" />
      </header>

      <main className="blog-split-grid">
        <nav>
          <strong>{t('toc.title', 'Inhaltsverzeichnis')}</strong>
          <ul>
            {tableOfContents.map((item, idx) => (
              <li key={item.id}><a href={`#${item.id}`}>{item.title}</a></li>
            ))}
          </ul>
        </nav>

        <article>
          <section id="problem">
            <h2 className="head">{t('problem.title', 'Das Problem: Unsichtbarkeit kostet Kunden')}</h2>
            <p className="text">{t('problem.text', 'Viele Handwerksbetriebe liefern Top-Leistung, bleiben aber online unsichtbar. Das Ergebnis: Die Konkurrenz gewinnt Aufträge, weil sie besser gefunden wird. Doch mit gezielter lokaler Suchmaschinenoptimierung (SEO) können Sie das ändern!')}</p>
            <img src={articleImageUrl} alt={t('problem.imageAlt', 'Lokales Geschäft in einer süddeutschen Stadt')} className="grid-spread" />
          </section>

          <section id="google-my-business">
            <h2 className="head">{t('googleMyBusiness.title', 'Google My Business: Ihr Tor zur lokalen Sichtbarkeit')}</h2>
            <p className="text">{t('googleMyBusiness.text', 'Das Google Business Profile (ehemals Google My Business) ist das mit Abstand wichtigste Werkzeug für lokales SEO. Es ist Ihr digitales Aushängeschild auf Google und in Google Maps. Ein vollständig optimiertes Profil mit aktuellen Öffnungszeiten, Dienstleistungen, Fotos Ihrer Arbeit und positiven Kundenbewertungen signalisiert Google, dass Sie ein relevantes und vertrauenswürdiges lokales Unternehmen sind. Dies ist der erste und wichtigste Schritt, um im begehrten „Local Pack“ – den drei Kartenergebnissen ganz oben – zu erscheinen.')}</p>
          </section>

          <div className="highlight-card grid-spread">
            <h3>{t('highlight.title', 'Der Enzlo-Vorteil')}</h3>
            <p>{t('highlight.text', 'Wir optimieren Ihr Google Business Profile bis ins kleinste Detail und entwickeln eine Strategie, um kontinuierlich neue, positive Bewertungen zu generieren. So werden Sie zur ersten Wahl in Ihrer Region.')}</p>
          </div>

          <section id="lokale-citations">
            <h2 className="head">{t('citations.title', 'Lokale Citations: Vertrauen und Konsistenz aufbauen')}</h2>
            <p className="text">{t('citations.text', 'Konsistenz ist der Schlüssel zum Vertrauen – für Kunden und für Google. Lokale „Citations“ sind Erwähnungen Ihres Unternehmens (Name, Adresse, Telefonnummer) in Online-Verzeichnissen wie Gelbe Seiten, Das Örtliche oder branchenspezifischen Portalen. Jeder konsistente Eintrag ist ein weiteres Signal an Google, dass Ihr Unternehmen legitim ist und an dem angegebenen Ort existiert. Inkonsistente Daten hingegen stiften Verwirrung und schaden Ihrem Ranking.')}</p>
          </section>

          <section id="content-strategie">
            <h2 className="head">{t('contentStrategy.title', 'Content-Strategie: Relevante Inhalte für lokale Kunden')}</h2>
            <p className="text">{t('contentStrategy.text', 'Erstellen Sie Inhalte, die die Fragen Ihrer lokalen Kunden beantworten. Ein Blogbeitrag über „Die 5 häufigsten Heizungsprobleme im Winter in München“ oder eine Projektseite über eine „Dachsanierung in Augsburg“ positioniert Sie als Experten und zieht gezielt lokale Suchanfragen an. Zeigen Sie Ihre Arbeit, erklären Sie Ihre Prozesse und geben Sie wertvolle Tipps. Dieser Content beweist Ihre Expertise und liefert Google frische, relevante Informationen, um Ihre Seite höher zu ranken.')}</p>
          </section>

          <section id="rezensionen">
            <h2 className="head">{t('reviews.title', 'Die Macht der Rezensionen')}</h2>
            <p className="text">{t('reviews.text', 'Positive Online-Bewertungen sind die digitale Form der Mundpropaganda und ein entscheidender Rankingfaktor. Ermutigen Sie zufriedene Kunden aktiv, eine Bewertung auf Google zu hinterlassen. Jede positive Rezension stärkt das Vertrauen potenzieller Neukunden und signalisiert Google, dass Sie qualitativ hochwertige Arbeit leisten. Reagieren Sie professionell auf alle Bewertungen – auch auf kritische. Das zeigt, dass Sie Kundenfeedback ernst nehmen.')}</p>
          </section>
          
          <section id="enzlo-loesung">
            <h2 className="head">{t('enzloSolution.title', 'Enzlo: Ihre All-in-One-Lösung')}</h2>
            <p className="text">{t('enzloSolution.text', 'Lokales SEO ist ein kontinuierlicher Prozess, kein einmaliges Projekt. Bei Enzlo nehmen wir Ihnen diese Arbeit ab. Wir entwickeln eine maßgeschneiderte lokale SEO-Strategie für Ihren Handwerksbetrieb, setzen sie um und sorgen dafür, dass Sie in Ihrer Region gefunden werden. Von der Optimierung Ihres Google-Profils über den Aufbau von Citations bis hin zur Content-Erstellung – wir sind Ihr Partner für nachhaltiges Wachstum.')}</p>
          </section>

          <section id="fazit">
            <h2 className="head">{t('conclusion.title', 'Fazit: Jetzt ist die Zeit zu handeln')}</h2>
            <p className="text">{t('conclusion.text1', 'Hören Sie auf, unsichtbar zu sein. Mit einer gezielten lokalen SEO-Strategie verwandeln Sie Ihre Online-Präsenz in einen zuverlässigen Motor zur Auftragsgewinnung. Dominieren Sie die lokalen Suchergebnisse in Süddeutschland und sorgen Sie dafür, dass der nächste Kunde, der nach Ihrem Handwerk sucht, Sie findet.')}</p>
            <p className="text"><strong>{t('conclusion.ctaTitle', 'Sind Sie bereit, Ihre lokale Konkurrenz hinter sich zu lassen?')}</strong> <Link to="/kontakt">{t('conclusion.ctaButton', 'Kontaktieren Sie uns noch heute')}</Link> {t('conclusion.ctaText', 'für eine kostenlose Analyse und Beratung.')}</p>
          </section>
        </article>
        <section id="faq">
          <div className="faq-card grid-spread">
            <h2>{t('faq.title', 'Häufige Fragen von Handwerkern zur lokalen SEO')}</h2>
            {[{
              q: t('faq.q1.question', 'Wie lange dauert es, bis lokale SEO-Maßnahmen wirken?'),
              a: t('faq.q1.answer', 'In der Regel zeigen sich erste Verbesserungen nach 2-3 Monaten. Für nachhaltige Top-Positionen sollten Sie 6-12 Monate einplanen.')
            }, {
              q: t('faq.q2.question', 'Brauche ich wirklich eine eigene Website?'),
              a: t('faq.q2.answer', 'Ja! Ein Google Business Profile ist wichtig, aber eine eigene Website gibt Ihnen die volle Kontrolle über Ihre Inhalte und stärkt Ihr Ranking.')
            }, {
              q: t('faq.q3.question', 'Wie bekomme ich mehr positive Bewertungen?'),
              a: t('faq.q3.answer', 'Bitten Sie zufriedene Kunden aktiv um eine Bewertung – am besten direkt nach Abschluss eines Projekts. Ein persönlicher Link per SMS oder E-Mail wirkt Wunder.')
            }, {
              q: t('faq.q4.question', 'Kann ich lokale SEO selbst machen?'),
              a: t('faq.q4.answer', 'Grundlagen wie Profilpflege und das Sammeln von Bewertungen können Sie selbst übernehmen. Für technische Optimierung und Strategie empfiehlt sich professionelle Unterstützung.')
            }].map((item, idx) => (
              <div key={idx} className="faq-item">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="faq-question">
                  {item.q}
                  <span>{openFaq === idx ? '−' : '+'}</span>
                </button>
                {openFaq === idx && <div className="faq-answer">{item.a}</div>}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default BlogPost_HandwerkerSEO;
